import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { TicketTimelineComponent } from '../ticket-timeline/ticket-timeline.component';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TicketService } from 'src/app/private/services/tickets.service';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-ticket-activity-tab',
    standalone: true,
    imports: [SharedModule, TicketTimelineComponent],
    templateUrl: './ticket-activity-tab.component.html',
    styleUrl: './ticket-activity-tab.component.scss'
})
export class TicketActivityTabComponent implements OnInit, OnChanges {
    @Input() ticket: any;
    @Input() currTabActiveId: any;
    @Input() isTableView: boolean = false;

    // Timeline data for the ticket
    ticketTimeline: any[] = [];

    // Loading state for activities
    isLoadingActivities: boolean = false;

    // Comment input
    newComment: string = '';

    // Dialog states
    showEditDialog: boolean = false;
    showDeleteDialog: boolean = false;
    selectedComment: any = null;
    editCommentText: string = '';
    isUpdatingComment: boolean = false;
    isDeletingComment: boolean = false;

    @ViewChild('activityContainer') activityContainer!: ElementRef;

    constructor(
        private localStorageService: LocalStorageService,
        private ticketService: TicketService,
        private messageService: MessageService
    ) { }

    ngOnInit() {
        if (this.ticket?.ticket_id) {
            this.initializeTimeline();
        }

        setTimeout(() => {
            const el = this.activityContainer.nativeElement;
            el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
            });
        }, 100);
    }

    ngOnChanges(changes: SimpleChanges) {
        setTimeout(() => {
            const el = this.activityContainer.nativeElement;
            el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
            });
        }, 100);
        if ((changes['ticket'] && changes['ticket'].currentValue )) {
            // Clear previous timeline data and reset loading state when ticket changes
            this.ticketTimeline = [];
            this.isLoadingActivities = false;
            this.initializeTimeline();
        }
    }

    initializeTimeline() {
        this.getTicketActivitiesData();
    }

    getTicketActivitiesData() {
        // Set loading state to true
        this.isLoadingActivities = true;

        const payload = {
            ticket_id: this.ticket.ticket_id,
            organization_id: this.localStorageService.getOrganizationId()
        };

        this.ticketService.getTicketActivities(payload).subscribe({
            next: (response) => {
                console.log('Ticket activities response:', response);

                this.ticketTimeline = response.rows.map((activity: any) => ({
                    id: activity.id, // Include the activity ID for edit/delete operations
                    name: `${activity.user?.fname ?? ''} ${activity.user?.lname ?? ''}`.trim() || 'Unknown User',
                    date: new Date(activity.createdAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    }),
                    desc: activity.comment_text || 'No description provided',
                    type: activity.comment_text ? 'comment' : 'activity' // Determine if it's a comment or activity
                }));

                // reverse the timeline to show latest activities first
                this.ticketTimeline.reverse();

                // Set loading state to false
                this.isLoadingActivities = false;

                setTimeout(() => {
                    const el = this.activityContainer.nativeElement;
                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior: 'smooth'
                    });
                }, 100);
            },
            error: (error) => {
                console.error('Error fetching ticket activities', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load ticket activities. Please try again later.'
                });

                // Set loading state to false even on error
                this.isLoadingActivities = false;
            }
        });
    }

    onKeyDown(event: KeyboardEvent) {
        // Send comment on Enter key press (without Shift)
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault(); // Prevent adding a new line
            this.addComment();
        }
    }

    addComment() {
        if (!this.newComment || !this.newComment.trim()) {
            return;
        }

        // Create new timeline entry for the comment
        const newTimelineEntry = {
            id: null, // Will be updated when API call succeeds
            name: this.localStorageService.getUserFullName() || 'Current User',
            date: new Date().toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }),
            desc: this.newComment.trim(),
            type: 'comment'
        };

        // Add to timeline
        this.ticketTimeline = [...this.ticketTimeline, newTimelineEntry];

        // Here you would make an API call to save the comment
        this.saveCommentToAPI(newTimelineEntry);

        // Clear the input
        this.newComment = '';

        setTimeout(() => {
            const el = this.activityContainer.nativeElement;
            el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
            });
        }, 100);


    }

    private saveCommentToAPI(comment: any) {
        const payload = {
            ticket_id: this.ticket.ticket_id,
            comment_text: comment.desc,
            source: 'manual',
            user_id: this.localStorageService.getUserId(),
            organization_id: this.localStorageService.getOrganizationId()
        };

        this.ticketService.addTicketComment(payload).subscribe({
            next: (response) => {
                console.log('Comment saved successfully', response);
                // Update the timeline entry with the returned ID
                const index = this.ticketTimeline.findIndex(item => item === comment);
                if (index !== -1) {
                    this.ticketTimeline[index] = { ...comment, id: response.id };
                }
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save comment. Please try again.'
                });
                console.error('Error saving comment', error);
                // Handle error - remove the comment from timeline or show error message
                this.ticketTimeline = this.ticketTimeline.filter(item => item !== comment);
            }
        });
    }

    onEditComment(event: any) {
        this.selectedComment = event;
        this.editCommentText = event.desc;
        this.showEditDialog = true;
    }

    onDeleteComment(event: any) {
        this.selectedComment = event;
        this.showDeleteDialog = true;
    }

    handleEditDialogClose() {
        this.showEditDialog = false;
        this.selectedComment = null;
        this.editCommentText = '';
    }

    handleDeleteDialogClose() {
        this.showDeleteDialog = false;
        this.selectedComment = null;
    }

    updateComment() {
        if (!this.editCommentText || !this.editCommentText.trim()) {
            return;
        }

        this.isUpdatingComment = true;

        // Update the comment in the timeline
        const index = this.ticketTimeline.findIndex(item => item === this.selectedComment);
        if (index !== -1) {
            this.ticketTimeline[index].desc = this.editCommentText.trim();
        }

        // Here you would make an API call to update the comment
        this.updateCommentToAPI(this.selectedComment, this.editCommentText.trim());
    }

    deleteComment() {
        if (!this.selectedComment) {
            return;
        }

        this.isDeletingComment = true;

        // Remove the comment from the timeline
        this.ticketTimeline = this.ticketTimeline.filter(item => item !== this.selectedComment);

        // Here you would make an API call to delete the comment
        this.deleteCommentFromAPI(this.selectedComment);
    }

    private updateCommentToAPI(comment: any, newText: string) {
       
        // Actual API call would be:
        
        const payload = {
            id: comment.id,
            comment_text: newText,
        };

        this.ticketService.updateTicketActivity(payload).subscribe({
            next: (response) => {
                this.isUpdatingComment = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Comment updated successfully'
                });
                this.handleEditDialogClose();
            },
            error: (error) => {
                this.isUpdatingComment = false;
                // Revert the change
                const index = this.ticketTimeline.findIndex(item => item.id === comment.id);
                if (index !== -1) {
                    this.ticketTimeline[index].desc = comment.desc;
                }
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update comment. Please try again.'
                });
            }
        });
        
    }

    private deleteCommentFromAPI(comment: any) {
       
        // Actual API call would be:
        const payload = {
            id: comment.id,
        };

        this.ticketService.deleteTicketActivity(payload).subscribe({
            next: (response) => {
                this.isDeletingComment = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Comment deleted successfully'
                });
                this.handleDeleteDialogClose();
            },
            error: (error) => {
                this.isDeletingComment = false;
                // Restore the comment
                this.ticketTimeline.push(comment);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete comment. Please try again.'
                });
            }
        });
    }
}
